import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, MessageCircle, FileText } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import WhatsAppComingSoonModal from './WhatsAppComingSoonModal';
import { WholesaleInvoiceContent } from './WholesaleInvoiceModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { formatPhoneForWhatsApp, formatWhatsAppMessage } from '../lib/whatsappMessage';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt2 = (n) => Number(n ?? 0).toFixed(2);
const fmt1 = (n) => Number(n ?? 0).toFixed(1);
const fmt0 = (n) => Math.round(Number(n ?? 0));
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
};

// Group items by gst_rate for the GST summary table
const buildGstSummary = (items) => {
  const map = {};
  for (const item of items) {
    const rate = item.gst_rate ?? 0;
    if (!map[rate]) map[rate] = { taxable: 0, gst: 0 };
    const gst     = item.gst_amount ?? 0;
    const taxable = (item.item_total ?? 0) - gst;
    map[rate].taxable += taxable;
    map[rate].gst     += gst;
  }
  return Object.entries(map)
    .filter(([rate]) => Number(rate) > 0)
    .map(([rate, val]) => ({
      rate:    Number(rate),
      taxable: Math.round(val.taxable * 100) / 100,
      gst:     Math.round(val.gst * 100) / 100,
      cgst:    Math.round(val.gst / 2 * 100) / 100,
      sgst:    Math.round(val.gst / 2 * 100) / 100,
    }));
};

const paymentLabel = (sale) => {
  if (sale.payment_mode === 'split' && sale.split_payments?.length) {
    return sale.split_payments
      .filter(p => p.amount > 0)
      .map(p => `${p.mode.toUpperCase()} ₹${fmt2(p.amount)}`)
      .join(' + ');
  }
  return (sale.payment_mode || 'cash').toUpperCase();
};

// ── Receipt content (shared between screen and print) ─────────────────────────

function ReceiptContent({ sale, storeProfile, is58 = false }) {
  const items           = sale.items ?? [];
  const gstSummary      = buildGstSummary(items);
  const totalGst        = items.reduce((s, i) => s + (i.gst_amount ?? 0), 0);
  // items_subtotal: sum of all item totals before overall discount
  const items_subtotal  = sale.subtotal ?? items.reduce((s, i) => s + (i.item_total ?? 0), 0);
  const subtotalExclGst = items_subtotal - totalGst;
  const overallDiscount = sale.overall_discount ?? 0;
  const roundOff        = sale.round_off ?? 0;
  const customer        = sale.customer_name || 'Walk-in';

  // Total savings = sum of per-item discounts + overall bill discount
  const itemSavings = items.reduce((s, item) => {
    const mrp = item.mrp ?? item.unit_price;
    return s + (mrp - item.unit_price) * item.quantity;
  }, 0);
  const totalSavings = Math.round((itemSavings + overallDiscount) * 100) / 100;

  const hasDiscount = items.some(i => (i.mrp ?? i.unit_price) - i.unit_price > 0.001) || overallDiscount > 0;
  const allIntQty   = items.length === 0 || items.every(i => Number.isInteger(Number(i.quantity)));
  const fmtQty      = (q) => allIntQty ? String(Math.round(Number(q))) : fmt1(q);

  return (
    <div className={`receipt-body font-mono ${is58 ? 'text-[10px]' : 'text-[11px]'} leading-snug text-black w-full max-w-full px-1`}>
      {/* Store header */}
      <div className="text-center mb-2">
        <div className="font-bold text-[16px] uppercase tracking-widest">
          {storeProfile?.shop_name || 'Store'}
        </div>
        {storeProfile?.address && (
          <div className="text-[10px] text-teal-600 mt-0.5">{storeProfile.address}</div>
        )}
        {storeProfile?.phone && (
          <div className="text-[10px] text-teal-600">Tel: {storeProfile.phone}</div>
        )}
        <div className="text-[10px] font-semibold mt-0.5">GSTIN: {storeProfile?.gstin || ''}</div>
        {storeProfile?.food_license_number && (
          <div className="text-[10px] font-semibold">FSSAI: {storeProfile.food_license_number}</div>
        )}
      </div>

      <div className="border-t-2 border-dashed border-black my-2" />

      {/* Bill info */}
      <div className="flex justify-between text-[10px] mb-0.5">
        <span>Invoice: <span className="font-bold">
          {sale.bill_number && sale.bill_number !== 'PENDING' ? sale.bill_number : '…'}
        </span></span>
        <span>{fmtDate(sale.created_at)}</span>
      </div>
      <div className="flex justify-between text-[10px] mb-1">
        <span>Customer: {customer}</span>
        <span>{fmtTime(sale.created_at)}</span>
      </div>

      <div className="border-t border-dashed border-black my-2" />

      {/* Items header */}
      <div className="flex gap-x-1 text-[10px] font-bold">
        <span className="flex-1">ITEM</span>
        <span className={is58 ? 'w-5 text-right' : 'w-6 text-right'}>QTY</span>
        <span className={is58 ? 'w-8 text-right' : 'w-9 text-right'}>MRP</span>
        {hasDiscount && <span className={is58 ? 'w-8 text-right' : 'w-9 text-right'}>RATE</span>}
        <span className={is58 ? 'w-9 text-right' : 'w-11 text-right'}>AMT</span>
      </div>

      <div className="border-t border-dashed border-black my-1" />

      {/* Items */}
      {items.map((item, i) => {
        const mrp      = item.mrp ?? item.unit_price;
        const itemHasDisc = mrp - item.unit_price > 0.001;
        return (
          <div key={i} className="mb-1">
            <div className="flex gap-x-1">
              <span className="flex-1 break-words min-w-0">{item.product_name}</span>
              <span className={is58 ? 'w-5 text-right shrink-0' : 'w-6 text-right shrink-0'}>{fmtQty(item.quantity)}</span>
              <span className={`${is58 ? 'w-8' : 'w-9'} text-right shrink-0 ${hasDiscount && itemHasDisc ? 'text-gray-500 line-through' : ''}`}>
                {fmt1(mrp)}
              </span>
              {hasDiscount && (
                <span className={is58 ? 'w-8 text-right shrink-0' : 'w-9 text-right shrink-0'}>{fmt1(item.unit_price)}</span>
              )}
              <span className={is58 ? 'w-9 text-right font-semibold shrink-0' : 'w-11 text-right font-semibold shrink-0'}>{fmt1(item.item_total)}</span>
            </div>
          </div>
        );
      })}

      <div className="border-t border-dashed border-black my-2" />

      {/* Totals */}
      <div className="space-y-0.5 text-[11px]">
        <div className="flex justify-between">
          <span>Items: {items.length}&nbsp;&nbsp;Qty: {items.reduce((s, i) => s + i.quantity, 0)}</span>
        </div>
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>₹{fmt2(items_subtotal)}</span>
        </div>
        {overallDiscount > 0 && (
          <div className="flex justify-between text-gray-700">
            <span>Cart Discount</span>
            <span>- ₹{fmt2(overallDiscount)}</span>
          </div>
        )}
        {roundOff !== 0 && (
          <div className="flex justify-between text-gray-500">
            <span>Round Off</span>
            <span>{roundOff > 0 ? '+' : ''}₹{fmt2(roundOff)}</span>
          </div>
        )}
      </div>

      <div className="border-t-2 border-black my-2" />

      <div className="flex justify-between font-bold text-[15px]">
        <span>TOTAL</span>
        <span>₹{fmt1(sale.total_amount)}</span>
      </div>

      {totalSavings > 0 && (
        <>
          <div className="border-t border-dashed border-black my-1.5" />
          <div className="text-center py-0.5">
            <div className="text-[11px] font-bold tracking-wide">** YOU SAVED ₹{fmt2(totalSavings)} ON THIS BILL! **</div>
          </div>
          <div className="border-t border-dashed border-black my-1.5" />
        </>
      )}

      {totalSavings === 0 && <div className="border-t-2 border-black my-2" />}

      <div className="flex justify-between text-[11px]">
        <span>Payment Mode</span>
        <span className="font-bold">{paymentLabel(sale)}</span>
      </div>

      {sale.customer_name && (() => {
        const khataCleared = sale.khata_cleared ?? 0;
        const khataAdded   = sale.khata_added ?? (
          sale.payment_mode === 'khata'
            ? sale.total_amount
            : (sale.split_payments?.reduce((s, p) => p.mode === 'khata' ? s + p.amount : s, 0) ?? 0)
        );
        // For fresh POS receipts, existing_khata_balance is set by handleCheckout.
        // For legacy SalesHistory cleared bills, derive it from stored khata_remaining + khata_cleared.
        const existingKhata = sale.existing_khata_balance
          ?? (khataCleared > 0 ? (sale.khata_remaining ?? 0) + khataCleared : 0);
        const netChange     = khataAdded - khataCleared;
        const newBalance    = existingKhata + netChange;
        const changeLabel   = khataAdded > 0 ? 'Khata Added'
                            : khataCleared > 0 ? 'Khata Cleared'
                            : 'Khata Added/Cleared';
        const changeDisplay = netChange > 0 ? `+₹${fmt2(khataAdded)}`
                            : netChange < 0 ? `-₹${fmt2(khataCleared)}`
                            : '0';
        return (
          <>
            <div className="border-t border-dashed border-black my-2" />
            <div className="text-[10px] space-y-0.5">
              <div className="flex justify-between">
                <span>{sale.customer_name} Existing Khata</span>
                <span>₹{fmt2(existingKhata)}</span>
              </div>
              <div className="flex justify-between">
                <span>{sale.customer_name} {changeLabel}</span>
                <span className="font-semibold">{changeDisplay}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Outstanding Balance</span>
                <span>₹{fmt2(newBalance)}</span>
              </div>
            </div>
            <div className="border-t-2 border-black my-1" />
            <div className="flex justify-between font-bold text-[13px]">
              <span>TOTAL COLLECTION</span>
              <span>₹{fmt2(sale.total_amount + khataCleared)}</span>
            </div>
            <div className="border-t-2 border-black my-1" />
          </>
        );
      })()}

      {/* GST summary table */}
      {gstSummary.length > 0 && (
        <>
          <div className="border-t border-dashed border-black my-2" />
          <div className="text-[10px]">
            <div className="font-bold mb-0.5">TAX SUMMARY</div>
            <div className="flex text-[9px] font-semibold border-b border-gray-400 pb-0.5 mb-0.5">
              <span className={is58 ? 'w-8' : 'w-10'}>Rate</span>
              <span className="flex-1 text-right">Taxable</span>
              <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>CGST</span>
              <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>SGST</span>
              <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>Total</span>
            </div>
            {gstSummary.map(g => (
              <div key={g.rate} className="flex text-[9px]">
                <span className={is58 ? 'w-8' : 'w-10'}>{g.rate}%</span>
                <span className="flex-1 text-right">{fmt2(g.taxable)}</span>
                <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>{fmt2(g.cgst)}</span>
                <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>{fmt2(g.sgst)}</span>
                <span className={`${is58 ? 'w-10' : 'w-14'} text-right`}>{fmt2(g.gst)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="border-t border-dashed border-black my-2" />

      {/* Footer */}
      <div className="text-center text-[10px] space-y-0.5">
        <div className="font-bold text-[11px]">{storeProfile?.receipt_footer || '** THANK YOU FOR SHOPPING WITH US! **'}</div>
        <div className="text-[9px] text-teal-600 mt-1">This is a computer generated receipt</div>
        <div className="text-[9px] text-gray-500">{storeProfile?.return_policy || 'Goods once sold will not be taken back'}</div>
      </div>

      {/* UPI Payment QR */}
      {storeProfile?.upi_id && (
        <>
          <div className="border-t border-dashed border-black my-2" />
          <div className="text-center">
            <div className="text-[10px] font-bold mb-1.5">Scan to Pay via UPI</div>
            <div className="flex justify-center">
              <QRCodeSVG
                value={`upi://pay?pa=${encodeURIComponent(storeProfile.upi_id)}&pn=${encodeURIComponent(storeProfile.shop_name || '')}&am=${Number(sale.total_amount ?? 0).toFixed(2)}&cu=INR&tn=Bill%20${encodeURIComponent(sale.bill_number || '')}`}
                size={96}
                level="M"
              />
            </div>
            <div className="text-[9px] text-gray-600 mt-1">UPI: {storeProfile.upi_id}</div>
          </div>
          <div className="border-t border-dashed border-black my-2" />
        </>
      )}

      {/* Powered by KiranaFlow */}
      <div className="mt-4 pt-2 border-t border-dashed border-gray-300 text-center" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <span className="text-[10px] font-bold text-gray-400 tracking-wide">Powered by </span>
        <span className="text-[10px] font-bold text-emerald-600 tracking-wide">KiranaFlow</span>
        <div className="text-[9px] text-gray-400 mt-0.5">kiranaflow.com</div>
      </div>
    </div>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────

export default function ReceiptModal({ sale, storeProfile, onClose }) {
  const [showWaModal, setShowWaModal] = useState(false);
  const [printMode, setPrintMode] = useState(
    storeProfile?.receipt_format === 'a4' ? 'invoice' : 'thermal'
  ); // 'thermal' | 'invoice'
  const isPending = sale.bill_number === 'PENDING';
  const printQueuedRef = useRef(false);
  const receiptPreviewRef = useRef(null);
  // Off-screen div rendered at full height for html2canvas capture — not clipped by the modal's overflow-y-auto.
  const captureRef = useRef(null);
  const modalRef = useRef(null);
  // window.print() is a silent no-op in Capacitor Android WebView, so use the
  // image-save path whenever we're inside a native app OR on a small screen.
  const isNativeApp = !!(window.Capacitor?.isNativePlatform?.());
  const isMobile = useIsMobile() || isNativeApp;

  const handlePrint = async () => {
    const tid = toast.loading('Connecting to printer…');
    try {
      const { printBluetooth } = await import('../lib/printUtils');
      const paperWidth = storeProfile?.receipt_format === '58mm' ? 58 : 80;
      await printBluetooth(sale, storeProfile, paperWidth);
      toast.dismiss(tid);
      toast.success('Sent to printer!');
    } catch (err) {
      toast.dismiss(tid);
      if (err.name !== 'AbortError') toast.error(err.message || 'Bluetooth print failed');
    }
  };

  const handleWhatsApp = async () => {
    const phone = formatPhoneForWhatsApp(sale.customer_mobile);
    if (phone) {
      // Customer has a phone on file — build the direct-chat link and call window.open
      // synchronously, with no dynamic import / await in between, so browsers don't
      // treat it as a delayed (non-gesture) popup and silently block it.
      try {
        const message = formatWhatsAppMessage(sale, storeProfile);
        const win = window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
        if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
      } catch (err) {
        toast.error('Could not open WhatsApp: ' + err.message);
      }
      return;
    }
    if (!isNativeApp) { setShowWaModal(true); return; }
    const tid = toast.loading('Preparing receipt…');
    try {
      const { captureElementAsImage, openWhatsApp } = await import('../lib/printUtils');
      // Capture from the off-screen div (full height, no overflow clip) not the scrollable preview.
      const blob = captureRef.current
        ? await captureElementAsImage(captureRef.current)
        : null;
      toast.dismiss(tid);
      await openWhatsApp(sale, storeProfile, blob);
    } catch (err) {
      toast.dismiss(tid);
      if (err.name !== 'AbortError') toast.error('Could not open WhatsApp: ' + err.message);
    }
  };

  // Move focus onto the modal so keystrokes (P/W/Escape, or anything else) don't
  // leak into whatever was focused behind it — e.g. the POS search bar staying
  // focused and silently receiving the typed letter as well as the shortcut firing.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    modalRef.current?.focus();
    return () => { previouslyFocused?.focus?.(); };
  }, []);

  // Auto-fire print once the real bill number arrives. Deferred a tick — see note below.
  useEffect(() => {
    if (!isPending && printQueuedRef.current) {
      printQueuedRef.current = false;
      setTimeout(() => window.print(), 0);
    }
  }, [isPending]);

  useEffect(() => {
    const handler = (e) => {
      // Swallow these keys entirely (even if focus somehow ended up on a background
      // field, e.g. the POS search bar) so they only ever act on this modal.
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        if (isPending) {
          printQueuedRef.current = true;
          toast.info('Print queued — will open once bill number is ready');
        } else {
          // window.print() opens a real, blocking native dialog. Calling it
          // synchronously from inside this handler re-enters the browser mid-dispatch,
          // which can let the keystroke's default action (character insertion) slip
          // into the still-focused POS search bar behind this modal despite
          // preventDefault() above. Deferring a tick lets this keydown event finish
          // dispatching normally first.
          setTimeout(() => window.print(), 0);
        }
        return;
      }
      if (e.key === 'w' || e.key === 'W') { e.preventDefault(); handleWhatsApp(); }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [onClose, isPending]);

  const isInvoice = printMode === 'invoice';
  const is58 = !isInvoice && storeProfile?.receipt_format === '58mm';

  return (
    <>
      {/* Print styles — switch @page size and visible wrapper based on mode */}
      <style>{`
        .receipt-print-wrapper { display: none; }
        .invoice-print-wrapper { display: none; }
        @media print {
          body > *:not(.receipt-print-wrapper):not(.invoice-print-wrapper) { display: none !important; }
          ${isInvoice
            ? `.invoice-print-wrapper { display: block !important; width: 100%; box-sizing: border-box; background: white; }
               @page { size: A4 portrait; margin: 12mm 14mm; }`
            : is58
              ? `.receipt-print-wrapper { display: block !important; width: 58mm; padding: 1mm 3mm; box-sizing: border-box; background: white; margin: 0; overflow: visible; }
                 @page { margin: 0; size: 58mm auto; }`
              : `.receipt-print-wrapper { display: block !important; width: 80mm; padding: 2mm 6mm; box-sizing: border-box; background: white; margin: 0; overflow: visible; }
                 @page { margin: 0; size: 80mm auto; }`
          }
        }
      `}</style>

      {/* Print portal — only one renders at a time based on mode */}
      {createPortal(
        isInvoice
          ? <div className="invoice-print-wrapper"><WholesaleInvoiceContent sale={sale} storeProfile={storeProfile} /></div>
          : <div className="receipt-print-wrapper"><ReceiptContent sale={sale} storeProfile={storeProfile} is58={is58} /></div>,
        document.body
      )}

      {/* Modal overlay */}
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          tabIndex={-1}
          className={`bg-white rounded-xl shadow-2xl w-full flex flex-col max-h-[90vh] transition-all duration-200 outline-none ${isInvoice ? 'max-w-2xl' : 'max-w-sm'}`}
        >

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              {isInvoice ? <FileText className="h-4 w-4" /> : <Printer className="h-4 w-4" />}
              {isInvoice ? 'Tax Invoice' : 'Bill Receipt'}
            </h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Print mode toggle */}
          <div className="px-4 pt-3 pb-2 shrink-0">
            <div className="flex rounded-lg border border-border overflow-hidden text-xs font-semibold">
              <button
                onClick={() => setPrintMode('thermal')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors ${
                  !isInvoice ? 'bg-gray-900 text-white' : 'text-muted-foreground hover:bg-muted/40'
                }`}
              >
                <Printer className="h-3.5 w-3.5" /> Thermal Receipt
              </button>
              <button
                onClick={() => setPrintMode('invoice')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors ${
                  isInvoice ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:bg-muted/40'
                }`}
              >
                <FileText className="h-3.5 w-3.5" /> Tax Invoice (A4)
              </button>
            </div>
          </div>

          {/* Scrollable preview */}
          {isInvoice ? (
            <div className="overflow-y-auto flex-1 px-6 py-4 bg-gray-100">
              <div ref={receiptPreviewRef} className="bg-white shadow-lg rounded p-6 mx-auto" style={{ maxWidth: '640px' }}>
                <WholesaleInvoiceContent sale={sale} storeProfile={storeProfile} />
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 px-4 py-4 bg-gray-50">
              <div ref={receiptPreviewRef} className="bg-white border border-gray-200 rounded px-5 py-4 shadow-xl">
                <ReceiptContent sale={sale} storeProfile={storeProfile} is58={is58} />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-5 py-3 border-t border-border flex gap-3 shrink-0">
            {isMobile ? (
              /* Mobile: direct BLE thermal print */
              <button
                onClick={handlePrint}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold ${
                  isInvoice
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'border border-border hover:bg-muted/60'
                }`}
              >
                <Printer className="h-4 w-4" />
                Print
              </button>
            ) : (
              /* Desktop: browser print dialog */
              <button
                onClick={() => {
                  if (!isInvoice && isPending) {
                    printQueuedRef.current = true;
                    toast.info('Print queued — will open once bill number is ready');
                  } else {
                    window.print();
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold ${
                  isInvoice
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'border border-border hover:bg-muted/60'
                }`}
              >
                {isInvoice ? <FileText className="h-4 w-4" /> : <Printer className="h-4 w-4" />}
                {!isInvoice && isPending ? 'Generating bill #…' : isInvoice ? 'Print Invoice (A4)' : 'Print / Save PDF'}
              </button>
            )}
            <button
              onClick={handleWhatsApp}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              <MessageCircle className="h-4 w-4" />
              Share via WhatsApp
            </button>
          </div>

          <p className="text-center text-xs text-muted-foreground pb-2">
            {isMobile ? 'W WhatsApp · Esc close' : 'P print · W WhatsApp · Esc close'}
          </p>
        </div>
      </div>

      {showWaModal && <WhatsAppComingSoonModal onClose={() => setShowWaModal(false)} />}

      {/* Off-screen receipt for WhatsApp image capture — full height, no overflow clip */}
      {createPortal(
        <div
          ref={captureRef}
          style={{ position: 'fixed', left: '-9999px', top: 0, width: is58 ? '220px' : '320px', background: '#fff', padding: '16px' }}
        >
          <ReceiptContent sale={sale} storeProfile={storeProfile} is58={is58} />
        </div>,
        document.body
      )}
    </>
  );
}
